import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export function ChangePassword() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  if (user === false) { nav("/login"); return null; }

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.new_password !== form.confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      toast.success("Password updated ✓"); nav("/dashboard");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="bg-[#0A0C10] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-2"><KeyRound size={14}/> Security</div>
          <h1 className="font-serif text-3xl mb-6">Change password</h1>
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="cp-current" type="password" required placeholder="Current password" value={form.current_password}
              onChange={(e) => setForm({...form, current_password: e.target.value})}
              className="w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input data-testid="cp-new" type="password" required minLength={6} placeholder="New password (min 6)" value={form.new_password}
              onChange={(e) => setForm({...form, new_password: e.target.value})}
              className="w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            <input data-testid="cp-confirm" type="password" required minLength={6} placeholder="Confirm new password" value={form.confirm}
              onChange={(e) => setForm({...form, confirm: e.target.value})}
              className="w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            {err && <div className="text-sm text-[#FF5A36]">{err}</div>}
            <button data-testid="cp-submit" type="submit" disabled={busy}
              className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-50">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [token, setToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [demoToken, setDemoToken] = useState("");
  const nav = useNavigate();

  const request = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.demo_token) { setDemoToken(data.demo_token); toast.success("Reset link generated"); }
      else toast.success("If the account exists, reset instructions will be sent.");
      setStep(2);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const reset = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPw });
      toast.success("Password reset — please sign in.");
      nav("/login");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="bg-[#0A0C10] border border-white/10 rounded-2xl p-8">
          <div className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-2">Recover</div>
          <h1 className="font-serif text-3xl mb-6">{step === 1 ? "Forgot your password?" : "Enter reset token"}</h1>

          {step === 1 && (
            <form onSubmit={request} className="space-y-4">
              <input data-testid="fp-email" type="email" required placeholder="Registered email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              {err && <div className="text-sm text-[#FF5A36]">{err}</div>}
              <button data-testid="fp-submit" type="submit" disabled={busy}
                className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy ? "Sending…" : "Send reset link"} <ArrowRight size={14}/>
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={reset} className="space-y-4">
              {demoToken && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300">
                  <div className="uppercase tracking-widest text-[10px] mb-1">Demo mode — token (auto-filled)</div>
                  <div className="font-mono break-all">{demoToken}</div>
                </div>
              )}
              <input data-testid="fp-token" required placeholder="Reset token"
                value={token || demoToken} onChange={(e) => setToken(e.target.value)}
                className="w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none font-mono text-xs"/>
              <input data-testid="fp-newpw" type="password" required minLength={6} placeholder="New password"
                value={newPw} onChange={(e) => setNewPw(e.target.value)}
                className="w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              {err && <div className="text-sm text-[#FF5A36]">{err}</div>}
              <button data-testid="fp-reset" type="submit" disabled={busy}
                className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-50">
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )}

          <div className="text-sm text-[#A0A4AB] text-center mt-6">
            Remembered? <Link to="/login" className="text-[#D4AF37]">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
